import { ViewportScroller } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { defaultLanguages, patterns } from '@constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AllDeviceInfo, Language } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { DeviceInfoService } from '@core/services/device-info.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { CommonsFacade } from '@state/commons';
import { RouterFacade } from '@state/router';
import { UserAuthFacade } from '@state/user-auth';
import { BehaviorSubject, combineLatest, of, ReplaySubject } from 'rxjs';
import { delay, delayWhen, distinctUntilChanged, map, startWith, take, takeUntil } from 'rxjs/operators';

const passwordsValidator = (formControl: AbstractControl): ValidationErrors | null => {
  const psswd = formControl.get('password')?.value;
  const confirmed = formControl.get('passwordConfirmation')?.value;
  return psswd === confirmed ? null : { noMatch: true };
};

const passwordConfirmationValidator = (formControl: AbstractControl): ValidationErrors | null => {
  const psswdFc = formControl.get('password');
  const confirmFc = formControl.get('passwordConfirmation');
  const psswd = psswdFc?.value;
  const confirmed = confirmFc?.value;
  const passwordsMatch = psswd === confirmed;

  if (!psswd?.length || !confirmed?.length) {
    return null;
  }

  if (!passwordsMatch) {
    confirmFc.markAsDirty();
    confirmFc.setErrors({ noMatch: true });
  }

  return passwordsMatch ? null : { noMatch: true };
};

@Component({
  selector: 'app-activate-account',
  templateUrl: './activate-account.component.html',
  styleUrls: [
    './activate-account.component.scss',
    './activate-account-desktop-shell.component.scss',
    './activate-account-mobile-shell.component.scss',
  ],
  standalone: false,
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly snackService = inject(StackedMessagesService);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly router = inject(RouterFacade);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly ls = inject(LocalStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly viewportScroller = inject(ViewportScroller);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly apiAvailable$ = new BehaviorSubject<boolean>(true);
  private readonly languageReady$ = new BehaviorSubject<boolean>(false);

  public activateSuccess$ = this.userAuthFacade.activateAccountSuccess$;
  public errorMessage$ = this.userAuthFacade.errorMessage$;
  public currentUserLang: Language;
  public authActionLoading$ = this.userAuthFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(300)))),
    startWith(false),
    takeUntil(this.destroy$)
  );

  // public authActionLoading$ = of(true);
  public commonsLoading$ = this.commonsFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(300)))),
    startWith(true),
    takeUntil(this.destroy$)
  );

  public isPasswordVisible = false;
  public isPasswordConfirmVisible = false;

  public APIAvailable = true;
  public deviceInfo: AllDeviceInfo;
  public isDesktopLayout = false;
  public viewLoading = true;

  public pinRequired = false;
  public gapRequired = true;
  public termsAcceptanceShownFully = false;

  private viewReadyTimer: ReturnType<typeof setTimeout> | null = null;

  public form: FormGroup<any> = this.fb.group(
    {
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(100),
          Validators.pattern(patterns.HasUpperCaseLetter),
          Validators.pattern(patterns.HasSpecialCharacter),
        ]),
      ],
      passwordConfirmation: new FormControl('', {
        validators: [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(100),
          Validators.pattern(patterns.HasUpperCaseLetter),
          Validators.pattern(patterns.HasSpecialCharacter),
        ],
        updateOn: 'change',
      }),
      hasAcceptedGDPR: [false, Validators.requiredTrue],
      hasAcceptedDataProcessing: [false, Validators.requiredTrue],
      activation_token: ['', Validators.compose([Validators.required])],
    },
    { validators: [passwordConfirmationValidator] }
  );

  ngOnInit(): void {
    this.setDeviceInfo(this.deviceInfoService.getInfo());

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.setDeviceInfo(info);
    });

    combineLatest([this.apiAvailable$, this.languageReady$, this.commonsLoading$.pipe(startWith(true))])
      .pipe(
        map(([APIAvailable, languageReady, commonsLoading]) => APIAvailable && (!languageReady || commonsLoading)),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(loading => this.setViewLoading(loading));

    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token || token.length < 20) {
      this.snackService.showError({ message: 'INVALID_ACTIVATION_TOKEN' });
      this.form.disable();
      return;
    }

    const pinRequired = this.route.snapshot.queryParamMap.get('pinRequired');
    if (pinRequired) {
      this.pinRequired = true;
      this.form.addControl(
        'pin',
        new FormControl('', {
          validators: [Validators.required, Validators.minLength(4), Validators.maxLength(100)],
        })
      );
    }

    const gap = this.route.snapshot.queryParamMap.get('gap');
    if (gap === 'false') {
      this.removeGapFieldsAndMakeNotRequired();
    }

    this.form.patchValue({
      activation_token: token,
    });

    this.commonsFacade.lastAPIPingSuccessfull$.pipe(takeUntil(this.destroy$)).subscribe(APIPingSuccess => {
      if (!APIPingSuccess) {
        this.setupUserLang([...defaultLanguages]);
        this.APIAvailable = false;
        this.apiAvailable$.next(false);
        return;
      }

      this.APIAvailable = true;
      this.apiAvailable$.next(true);
    });

    this.commonsFacade
      .getLanguages$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(availableLanguages => {
        this.setupUserLang([...availableLanguages]);
      });
  }

  ngOnDestroy(): void {
    this.userAuthFacade.resetErrorMessage();
    this.clearViewReadyTimer();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public submitForm(): void {
    if (this.form.invalid) {
      return;
    }

    const psswdsValid = passwordsValidator(this.form);
    if (psswdsValid !== null) {
      this.form.setErrors(psswdsValid);
      return;
    }

    this.userAuthFacade.activateAccountError$
      .pipe(take(1), takeUntil(this.activateSuccess$), takeUntil(this.destroy$))
      .subscribe(activateError => {
        this.snackService.showError({ message: activateError.errorMessage });
      });

    this.activateSuccess$
      .pipe(take(1), takeUntil(this.userAuthFacade.activateAccountError$), takeUntil(this.destroy$))
      .subscribe(() => {
        this.snackService.showInfo({
          message: 'COMMON.NOTIFICATION.ACCOUNT_ACTIVATED_YOU_CAN_NOW_LOGIN_USING_YOUR_PASSWORD',
        });
        this.router.changeRoute({ linkParams: ['login'] });
      });

    this.errorMessage$.pipe(take(1)).subscribe(errorMessage => {
      if (!errorMessage) {
        return;
      }
      setTimeout(() => this.viewportScroller.scrollToAnchor('activateAccountErrorMessage'), 200);
    });

    this.userAuthFacade.activateAccount(this.form.value);
    return;
  }

  private removeGapFieldsAndMakeNotRequired(): void {
    const gdprFc = this.form.get('hasAcceptedGDPR');
    const dataFc = this.form.get('hasAcceptedDataProcessing');

    gdprFc.clearValidators();
    gdprFc.markAsPristine();
    gdprFc.updateValueAndValidity();
    dataFc.clearValidators();
    dataFc.markAsPristine();
    dataFc.updateValueAndValidity();

    this.form.updateValueAndValidity();
    this.gapRequired = false;
  }

  private setupUserLang(langs: Language[]): void {
    const langKey = this.route.snapshot.queryParamMap.get('langKey');

    // lang from query params is most important
    const langInQueryParams = langs.find(({ key }) => key === langKey);

    // if no lang in query params then localStorage
    const langInLS = this.ls.getUserLang();

    // if no lang in Query & no in LS use default
    const defaultLang = langs.find(({ key }) => key === 'en');

    // 1. langInQuery 2. langInLS 3. defaultLang
    const langToUse = langInQueryParams?.key ? langInQueryParams : langInLS?.key ? langInLS : defaultLang;

    this.ls.setUserLang(langToUse);
    this.translateService.use(langToUse.key);
    this.currentUserLang = langToUse;
    this.languageReady$.next(!!langToUse?.key);
  }

  public userLangChanged($event: Language): void {
    this.currentUserLang = $event;
    this.languageReady$.next(!!$event?.key);
  }

  public scrollToInput(event: FocusEvent) {
    if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
      const target = event.target as HTMLElement;
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }

  private setDeviceInfo(info: AllDeviceInfo): void {
    this.deviceInfo = info;
    this.isDesktopLayout = info.deviceTypeDetected === 'DESKTOP';
  }

  private setViewLoading(loading: boolean): void {
    if (loading) {
      this.clearViewReadyTimer();
      this.viewLoading = true;
      return;
    }

    this.scheduleViewReady();
  }

  private scheduleViewReady(): void {
    if (this.viewReadyTimer || !this.viewLoading) {
      return;
    }

    this.viewReadyTimer = setTimeout(() => {
      this.viewLoading = false;
      this.viewReadyTimer = null;
    });
  }

  private clearViewReadyTimer(): void {
    if (!this.viewReadyTimer) {
      return;
    }

    clearTimeout(this.viewReadyTimer);
    this.viewReadyTimer = null;
  }
}
